import { useCallback, useEffect, useRef, useState } from "react";
import { canQueryPermissions, hasMediaAccess } from "../helpers";

const DEVICE_POLLING_INTERVAL_MS = 2000;

const testHasDeviceAccess = async () => {
  try {
    await navigator.mediaDevices.getUserMedia({
      video: true,
      audio: true,
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const useMediaDevices = (isHost: boolean) => {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [isPermissionQueryAvailable, setIsPermissionQueryAvailable] =
    useState(true);
  const hasDeviceAccessRef = useRef(true);
  const isPollingDevices = useRef(false);
  const pollDevicesTimeout = useRef<NodeJS.Timeout | null>(null);

  const enumerateMediaDevices = useCallback(async () => {
    if (hasMediaAccess()) {
      hasDeviceAccessRef.current = await testHasDeviceAccess();
      const foundDevices = await navigator.mediaDevices.enumerateDevices();
      setDevices(foundDevices);
      return foundDevices;
    }
    return [];
  }, []);

  const pollMediaDevices = useCallback(() => {
    isPollingDevices.current = true;
    pollDevicesTimeout.current = null;
    const areDevicesAvailable = (devices: MediaDeviceInfo[]) =>
      !!devices?.length && devices.some(device => !!device.label?.trim());
    if (areDevicesAvailable(devices)) {
      return;
    }
    enumerateMediaDevices()
      .then(foundDevices => {
        if (areDevicesAvailable(foundDevices)) {
          isPollingDevices.current = false;
        } else {
          pollDevicesTimeout.current = setTimeout(() => {
            pollMediaDevices();
          }, DEVICE_POLLING_INTERVAL_MS);
        }
      })
      .catch(error => {
        console.error(error);
      });
  }, [enumerateMediaDevices, devices]);

  const handleDeviceChange = useCallback(
    () => void enumerateMediaDevices(),
    [enumerateMediaDevices],
  );

  useEffect(() => {
    if (isHost && hasMediaAccess()) {
      handleDeviceChange();
      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange,
      );
    }
    return () =>
      navigator.mediaDevices.removeEventListener(
        "devicechange",
        handleDeviceChange,
      );
  }, [isHost, handleDeviceChange]);

  const setupPermissionListener = useCallback(
    (permissionName: PermissionName, errorContext: string) =>
      navigator.permissions
        .query({ name: permissionName })
        .then(permissionStatus => {
          permissionStatus.onchange = () => {
            if (permissionStatus.state === "granted") {
              void enumerateMediaDevices();
            }
          };
          return permissionStatus;
        })
        .catch(e => {
          if (e instanceof Error) {
            console.error(errorContext, e.message);
          }
          setIsPermissionQueryAvailable(false);
          return null;
        }),
    [enumerateMediaDevices],
  );

  useEffect(() => {
    if (!canQueryPermissions()) {
      return;
    }
    let cameraPermissionStatus: PermissionStatus | null = null;
    let microphonePermissionStatus: PermissionStatus | null = null;

    // Listen for camera permission changes
    void setupPermissionListener(
      "camera" as PermissionName,
      "Could not listen for camera permission status",
    ).then(status => {
      cameraPermissionStatus = status;
    });

    // Listen for microphone permission changes
    void setupPermissionListener(
      "microphone" as PermissionName,
      "Could not listen for microphone permission status",
    ).then(status => {
      microphonePermissionStatus = status;
    });

    return () => {
      if (cameraPermissionStatus) {
        cameraPermissionStatus.onchange = null;
      }
      if (microphonePermissionStatus) {
        microphonePermissionStatus.onchange = null;
      }
    };
  }, [setupPermissionListener]);

  // Check if we need to poll for devices
  useEffect(() => {
    if (isPermissionQueryAvailable || isPollingDevices.current) {
      return;
    }
    pollMediaDevices();
  }, [isPermissionQueryAvailable, pollMediaDevices]);

  // Clear the timeout when unmounting
  useEffect(
    () => () => {
      if (pollDevicesTimeout.current) {
        clearTimeout(pollDevicesTimeout.current);
      }
    },
    [],
  );

  return {
    hasAccess: hasDeviceAccessRef.current,
    devices,
  };
};
